import * as React from 'react';
import { Link } from 'react-router';
import {
  CheckCircle2Icon,
  CircleIcon,
  ClockIcon,
  LockIcon,
  RadioIcon,
  SparklesIcon,
  UserIcon,
  XCircleIcon,
} from 'lucide-react';
import type { PublicChallenge } from '@cdf/api-client-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useHuntChallenges } from '@/hooks/use-hunt-challenges';
import {
  formatScheduleTime12,
  parseScheduleMinutes,
  type ScheduleStatus,
} from '@/hooks/use-time-based-hunt';

export function isSessionQuizChallenge(challenge: PublicChallenge): boolean {
  if (challenge.type !== 'quiz') return false;
  return Boolean(
    challenge.sessionQuestion ||
      challenge.room ||
      challenge.presenter ||
      challenge.startTime ||
      challenge.endTime,
  );
}

export function getSessionQuizQuestion(challenge: PublicChallenge): string | null {
  const fromConfig = challenge.config?.question?.trim();
  if (fromConfig) return fromConfig;
  const fromSession = challenge.sessionQuestion?.trim();
  return fromSession || null;
}

export function getSessionQuizOptions(challenge: PublicChallenge): string[] {
  return challenge.config?.options?.filter((o) => o.trim().length > 0) ?? [];
}

function formatSlot(startTime?: string | null, endTime?: string | null): string | null {
  const start = parseScheduleMinutes(startTime);
  const end = parseScheduleMinutes(endTime);
  if (start == null && end == null) return null;
  if (start != null && end != null) {
    return `${formatScheduleTime12(start)} – ${formatScheduleTime12(end)}`;
  }
  if (start != null) return `From ${formatScheduleTime12(start)}`;
  return `Until ${formatScheduleTime12(end!)}`;
}

function remainingUntilEnd(
  endTime: string | null | undefined,
  selectedMinutes: number | undefined,
): number | null {
  if (selectedMinutes == null) return null;
  const end = parseScheduleMinutes(endTime);
  if (end == null) return null;
  return end - selectedMinutes;
}

function formatCountdown(remainingMinutes: number): string {
  if (remainingMinutes <= 0) return 'Ending now';
  const h = Math.floor(remainingMinutes / 60);
  const m = remainingMinutes % 60;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m} min left`;
}

const STATUS_STYLES: Record<
  Exclude<ScheduleStatus, 'unscheduled'>,
  { label: string; className: string }
> = {
  live: {
    label: 'Live now',
    className: 'border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  },
  upcoming: {
    label: 'Upcoming',
    className: 'border-transparent bg-sky-500/15 text-sky-700 dark:text-sky-400',
  },
  ended: {
    label: 'Ended',
    className: 'border-transparent bg-muted text-muted-foreground',
  },
};

export interface SessionQuizCardProps {
  challenge: PublicChallenge;
  /** When true, show completed state even before local answer. */
  completed?: boolean;
  /** Event-clock minutes for remaining-time countdown. */
  selectedMinutes?: number;
  scheduleStatus?: ScheduleStatus;
  /** Hunt slug for join CTA when participant is not signed into the hunt. */
  huntSlug?: string;
  compact?: boolean;
  className?: string;
  /** Hide outer chrome when nested inside another card. */
  bare?: boolean;
}

/**
 * Session attendance quiz — presenter/session metadata, multiple-choice
 * question, countdown while live, and completion via hunt challenge API.
 */
export function SessionQuizCard({
  challenge,
  completed = false,
  selectedMinutes,
  scheduleStatus,
  huntSlug,
  compact = false,
  className,
  bare = false,
}: SessionQuizCardProps) {
  const [selectedAnswer, setSelectedAnswer] = React.useState<number | null>(null);
  const [answeredCorrectly, setAnsweredCorrectly] = React.useState(false);
  const { session, completeChallenge, isCompleting, progress } = useHuntChallenges();

  const progressCompleted =
    completed ||
    (progress?.completedChallengeIds.includes(challenge.id) ?? false) ||
    answeredCorrectly;

  const question = getSessionQuizQuestion(challenge);
  const options = getSessionQuizOptions(challenge);
  const correctIndex = challenge.config?.correctIndex;
  const hasQuiz = Boolean(question && options.length > 0 && correctIndex != null);
  const slotLabel = formatSlot(challenge.startTime, challenge.endTime);
  const remaining = remainingUntilEnd(challenge.endTime, selectedMinutes);
  const statusMeta =
    scheduleStatus && scheduleStatus !== 'unscheduled'
      ? STATUS_STYLES[scheduleStatus]
      : null;
  const canAnswer =
    Boolean(session) &&
    hasQuiz &&
    !progressCompleted &&
    scheduleStatus !== 'ended' &&
    scheduleStatus !== 'upcoming';

  const handleAnswer = (index: number) => {
    if (!canAnswer || isCompleting) return;
    setSelectedAnswer(index);
    if (correctIndex === index) {
      setAnsweredCorrectly(true);
      completeChallenge(challenge.id, { selectedAnswer: index });
    }
  };

  const body = (
    <div className={cn('space-y-3', compact && 'space-y-2', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {progressCompleted ? (
              <Badge className="gap-1 bg-emerald-600 hover:bg-emerald-600">
                <CheckCircle2Icon className="h-3 w-3" />
                Completed
              </Badge>
            ) : statusMeta ? (
              <Badge variant="outline" className={cn('gap-1', statusMeta.className)}>
                {scheduleStatus === 'live' && <RadioIcon className="h-3 w-3" />}
                {statusMeta.label}
              </Badge>
            ) : (
              <Badge variant="secondary" className="gap-1">
                <SparklesIcon className="h-3 w-3" />
                Session quiz
              </Badge>
            )}
            <Badge variant="outline">+{challenge.points} pts</Badge>
          </div>
          <p className={cn('font-semibold leading-snug', compact ? 'text-sm' : 'text-base')}>
            {challenge.title}
          </p>
        </div>
      </div>

      <div
        className={cn(
          'grid gap-1.5 rounded-md border border-border/70 bg-muted/30',
          compact ? 'p-2 text-[11px]' : 'p-2.5 text-xs',
        )}
      >
        {challenge.room && (
          <div className="flex items-start justify-between gap-2">
            <span className="text-muted-foreground">Room</span>
            <span className="font-medium text-right">{challenge.room}</span>
          </div>
        )}
        {slotLabel && (
          <div className="flex items-start justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <ClockIcon className="h-3 w-3" aria-hidden />
              Slot
            </span>
            <span className="font-medium text-right">{slotLabel}</span>
          </div>
        )}
        {challenge.presenter && (
          <div className="flex items-start justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <UserIcon className="h-3 w-3" aria-hidden />
              Presenter
            </span>
            <span className="font-medium text-right">{challenge.presenter}</span>
          </div>
        )}
        {scheduleStatus === 'live' && remaining != null && remaining >= 0 && !progressCompleted && (
          <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-1.5">
            <span className="text-muted-foreground">Session clock</span>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">
              {formatCountdown(remaining)}
            </span>
          </div>
        )}
      </div>

      {challenge.instructions && !compact && (
        <p className="text-xs text-muted-foreground leading-relaxed">{challenge.instructions}</p>
      )}

      {progressCompleted ? (
        <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm">
          <CheckCircle2Icon className="h-4 w-4 shrink-0 text-emerald-600" />
          <span className="font-medium text-emerald-800 dark:text-emerald-300">
            Attendance quiz complete — +{challenge.points} pts earned
          </span>
        </div>
      ) : !session ? (
        <div className="space-y-2 rounded-md border border-dashed border-border p-3">
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <LockIcon className="h-3.5 w-3.5" />
            Join the hunt to answer this session quiz and earn points.
          </p>
          {huntSlug && (
            <Button asChild size="sm" className="w-full">
              <Link to={`/hunt/${huntSlug}`}>Open hunt passport</Link>
            </Button>
          )}
        </div>
      ) : scheduleStatus === 'upcoming' ? (
        <p className="text-xs text-muted-foreground">
          This session hasn&apos;t started on the event clock yet. Drag the timeline forward or wait
          until it goes live.
        </p>
      ) : scheduleStatus === 'ended' ? (
        <p className="text-xs text-muted-foreground">
          This session window has ended on the event clock. Scrub the timeline back to practice, or
          catch the next live slot.
        </p>
      ) : !hasQuiz ? (
        <Badge variant="outline">Quiz not configured</Badge>
      ) : (
        <div className="space-y-2">
          <p className={cn('font-medium', compact ? 'text-xs' : 'text-sm')}>{question}</p>
          <div className="space-y-1.5">
            {options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = correctIndex === index;
              const showCorrect = answeredCorrectly && isCorrect;
              const showWrong = isSelected && !isCorrect;
              return (
                <button
                  key={`${challenge.id}-opt-${index}`}
                  type="button"
                  onClick={() => handleAnswer(index)}
                  disabled={answeredCorrectly || isCompleting || !canAnswer}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg border text-left transition-colors',
                    compact ? 'px-2.5 py-1.5 text-xs' : 'p-2.5 text-sm',
                    showCorrect && 'border-green-500 bg-green-500/10',
                    showWrong && 'border-red-500 bg-red-500/10',
                    !isSelected && !answeredCorrectly && 'hover:bg-muted',
                    answeredCorrectly && !isCorrect && 'opacity-50',
                  )}
                >
                  <span className="min-w-0 flex-1">{option}</span>
                  {showCorrect && <CheckCircle2Icon className="h-4 w-4 shrink-0 text-green-500" />}
                  {showWrong && <XCircleIcon className="h-4 w-4 shrink-0 text-red-500" />}
                  {!showCorrect && !showWrong && (
                    <CircleIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                  )}
                </button>
              );
            })}
          </div>
          {selectedAnswer !== null && !answeredCorrectly && (
            <p className="text-xs text-muted-foreground">Not quite — try again!</p>
          )}
          {isCompleting && (
            <p className="text-xs text-muted-foreground">Saving your answer…</p>
          )}
        </div>
      )}
    </div>
  );

  if (bare) return body;

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card',
        progressCompleted && 'border-emerald-500/40 bg-emerald-500/5',
        compact ? 'p-2.5' : 'p-3',
        className,
      )}
    >
      {body}
    </div>
  );
}
